import { useUniversalAuth } from "@west-tennessee-consulting/ui";

// Utility function for making authenticated API calls to QuickBooks endpoints
export const useAuthenticatedFetch = () => {
  const { session } = useUniversalAuth();

  return (url: string, options: RequestInit = {}) => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...(options.headers as Record<string, string>),
    };

    if (session?.access_token) {
      headers.Authorization = `Bearer ${session.access_token}`;
    }

    return fetch(url, {
      ...options,
      headers,
    });
  };
};

/**
 * Pull the `{ error }` message out of a failed route's JSON body.
 *
 * `Response.json()` is typed loosely by the DOM lib, so the body is treated as
 * `unknown` and narrowed here - a route that answers with a non-JSON error page
 * (a login redirect, a gateway 502) must produce the fallback message, not a
 * TypeError thrown from inside the error path.
 */
function errorTextFrom(body: unknown): string | undefined {
  if (typeof body === "object" && body !== null && "error" in body) {
    const { error } = body;
    return typeof error === "string" ? error : undefined;
  }
  return undefined;
}

// Simple helper for authenticated QuickBooks API calls
export const makeAuthenticatedQuickBooksCall = async (
  endpoint: string,
  /** Serialized straight to the request body - the route owns its own schema. */
  body: unknown,
  authToken?: string
) => {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  const response = await fetch(`/api/quickbooks/${endpoint}`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorBody: unknown = await response.json().catch(() => ({}));
    throw new Error(errorTextFrom(errorBody) || `Failed to call ${endpoint}`);
  }

  return response.json();
};

// Authenticated GET helper for QuickBooks API routes (no body)
export const makeAuthenticatedQuickBooksGet = async (endpoint: string, authToken?: string) => {
  const headers: Record<string, string> = {};
  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }
  const response = await fetch(`/api/quickbooks/${endpoint}`, {
    method: "GET",
    headers,
  });
  if (!response.ok) {
    const errorBody: unknown = await response.json().catch(() => ({}));
    throw new Error(errorTextFrom(errorBody) || `Failed to call ${endpoint}`);
  }
  return response.json();
};
